import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BulkUploadModal = ({ isOpen, onClose, onBulkUpload }) => {
  const [csvData, setCsvData] = useState('');
  const [parseError, setParseError] = useState('');

  if (!isOpen) return null;

  const handleFileUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setCsvData(event?.target?.result);
        setParseError('');
      };
      reader?.readAsText(file);
    }
  };

  const parseCsvData = () => {
    try {
      const lines = csvData?.trim()?.split('\n');
      if (lines?.length < 2) {
        setParseError('CSV file must contain header row and at least one data row');
        return null;
      }

      const headers = lines?.[0]?.split(',')?.map(h => h?.trim()?.toLowerCase());
      const requiredHeaders = ['content', 'platforms', 'date', 'time'];
      
      const missingHeaders = requiredHeaders?.filter(h => !headers?.includes(h));
      if (missingHeaders?.length > 0) {
        setParseError(`Missing required columns: ${missingHeaders?.join(', ')}`);
        return null;
      }

      const posts = [];
      for (let i = 1; i < lines?.length; i++) {
        const values = lines?.[i]?.split(',')?.map(v => v?.trim());
        if (values?.length !== headers?.length) continue;

        const post = {};
        headers?.forEach((header, index) => {
          post[header] = values?.[index];
        });

        const [year, month, day] = post?.date?.split('-');
        const [hours, minutes] = post?.time?.split(':');
        const scheduledDate = new Date(year, month - 1, day, hours, minutes);

        posts?.push({
          id: Date.now() + i,
          content: post?.content,
          platforms: post?.platforms?.split('|')?.map(p => p?.trim()?.toLowerCase()),
          media: post?.media || null,
          mediaAlt: post?.mediaalt || `Bulk uploaded social media post image for ${post?.platforms}`,
          scheduledDate: scheduledDate,
          status: 'scheduled',
          recurring: false
        });
      }

      return posts;
    } catch (error) {
      setParseError('Error parsing CSV file. Please check the format.');
      return null;
    }
  };

  const handleUpload = () => {
    const posts = parseCsvData();
    if (posts && posts?.length > 0) {
      onBulkUpload(posts);
      setCsvData('');
      setParseError('');
      onClose();
    }
  };

  const downloadTemplate = () => {
    const template = `content,platforms,date,time,media,mediaalt
"Check out our new product launch!",facebook|instagram,2025-12-15,14:00,,Product launch announcement image showing new smartphone with modern design "Join us for our webinar on digital marketing",linkedin|twitter,2025-12-16,10:00,,Professional webinar promotional banner with speaker headshots and event details "Weekend sale - 50% off everything!",facebook|instagram|twitter,2025-12-20,09:00,,Vibrant sale banner with bold discount text and colorful product collage`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_upload_template.csv';
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-modal w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Upload" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Bulk Upload Posts</h2>
              <p className="text-sm text-muted-foreground">Upload multiple posts via CSV file</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-accent flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-accent mb-2">CSV Format Requirements</h3>
                <ul className="text-xs text-accent space-y-1">
                  <li>• Required columns: content, platforms, date, time</li>
                  <li>• Optional columns: media, mediaalt</li>
                  <li>• Platforms should be separated by | (e.g., facebook|instagram)</li>
                  <li>• Date format: YYYY-MM-DD</li>
                  <li>• Time format: HH:MM (24-hour)</li>
                  <li>• Always provide mediaalt when including media URLs</li>
                </ul>
              </div>
            </div>
          </div>

          <div>
            <Button
              variant="outline"
              onClick={downloadTemplate}
              iconName="Download"
              iconPosition="left"
              fullWidth
            >
              Download CSV Template
            </Button>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Upload CSV File
            </label>
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary transition-colors">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <Icon name="FileText" size={32} className="text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-foreground font-medium mb-1">
                  Click to upload CSV file
                </p>
                <p className="text-xs text-muted-foreground">
                  CSV files only
                </p>
              </label>
            </div>
          </div>

          {csvData && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                CSV Preview
              </label>
              <div className="bg-muted rounded-lg p-4 max-h-48 overflow-y-auto">
                <pre className="text-xs text-foreground whitespace-pre-wrap font-mono">
                  {csvData?.split('\n')?.slice(0, 10)?.join('\n')}
                  {csvData?.split('\n')?.length > 10 && '\n...'}
                </pre>
              </div>
            </div>
          )}

          {parseError && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
                <p className="text-sm text-error">{parseError}</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleUpload}
            disabled={!csvData}
            iconName="Upload"
            iconPosition="left"
          >
            Upload Posts
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;